import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Landmark, Save, Image as ImageIcon, Trash2, Upload } from "lucide-react";
import { isValidVatIT } from "../lib/validators";

const FIELDS = [
  { key: "name", label: "Ragione sociale", full: true },
  { key: "address", label: "Indirizzo", full: true },
  { key: "cap", label: "CAP" },
  { key: "city", label: "Comune" },
  { key: "province", label: "Prov." },
  { key: "country", label: "Stato" },
  { key: "vat", label: "P. IVA" },
  { key: "tax_code", label: "Cod. Fiscale" },
  { key: "email", label: "Email", type: "email" },
  { key: "phone", label: "Telefono" },
  { key: "website", label: "Sito web" },
  { key: "general_terms", label: "Condizioni generali d'ordine (stampate in calce a ogni ordine)", full: true, type: "textarea" },
];

export default function AziendaProfile() {
  const [form, setForm] = useState(null);
  const [logo, setLogo] = useState("");
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/company/profile").then(({ data }) => {
      const f = {};
      FIELDS.forEach((x) => { f[x.key] = data[x.key] || ""; });
      if (!f.country) f.country = "Italia";
      setForm(f);
      setLogo(data.logo || "");
    }).catch(() => toast.error("Impossibile caricare l'anagrafica azienda"));
  }, []);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!form.name || !form.name.trim()) { toast.error("La ragione sociale è obbligatoria"); return; }
    setSaving(true);
    try { await api.put("/company/profile", form); toast.success("Anagrafica azienda salvata"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const onLogoPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Seleziona un'immagine"); return; }
    if (file.size > 800 * 1024) { toast.error("Logo troppo grande (max 800KB)"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result;
      try { await api.post("/company/logo", { logo: dataUrl }); setLogo(dataUrl); toast.success("Logo caricato"); }
      catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    };
    reader.readAsDataURL(file);
  };
  const removeLogo = async () => {
    try { await api.delete("/company/logo"); setLogo(""); toast.success("Logo rimosso"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const inp = "w-full border border-stone-300 bg-white px-3 py-2 text-sm rounded-sm outline-none focus:ring-2 focus:ring-brand/40";
  if (!form) return <div className="text-stone-400 font-serif text-xl">Caricamento…</div>;
  return (
    <div className="space-y-8 max-w-3xl">
      <div>
        <p className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Impostazioni</p>
        <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tight text-stone-900">Anagrafica azienda</h1>
        <p className="text-sm text-stone-500 mt-2">Questi dati e il logo vengono usati nell'intestazione di tutti i report stampati (PDF/Excel) e negli ordini ai fornitori.</p>
      </div>

      <div className="bg-white border border-stone-200 rounded-sm p-6">
        <div className="flex items-center gap-2 mb-5"><ImageIcon size={18} className="text-brand" /><h3 className="font-serif text-2xl text-stone-900">Logo aziendale</h3></div>
        <div className="flex items-center gap-6">
          <div className="w-28 h-28 border border-stone-200 rounded-sm flex items-center justify-center bg-stone-50 overflow-hidden">
            {logo ? <img data-testid="logo-preview" src={logo} alt="Logo" className="max-w-full max-h-full object-contain" /> : <ImageIcon size={32} className="text-stone-300" />}
          </div>
          <div className="flex flex-col gap-2">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="logo-file-input" onChange={onLogoPick} />
            <button data-testid="logo-upload-btn" onClick={() => fileRef.current?.click()} className="rounded-full border border-brand text-brand px-4 py-2 text-sm font-semibold flex items-center gap-2 hover:bg-brand hover:text-white transition-colors"><Upload size={16} /> Carica logo</button>
            {logo && <button data-testid="logo-remove-btn" onClick={removeLogo} className="rounded-full border border-stone-300 text-stone-500 px-4 py-2 text-sm font-semibold flex items-center gap-2 hover:bg-stone-100"><Trash2 size={16} /> Rimuovi</button>}
            <p className="text-xs text-stone-400">PNG o JPG, max 800KB.</p>
          </div>
        </div>
      </div>

      <div className="bg-white border border-stone-200 rounded-sm p-6">
        <div className="flex items-center gap-2 mb-5"><Landmark size={18} className="text-brand" /><h3 className="font-serif text-2xl text-stone-900">Dati aziendali</h3></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {FIELDS.map((x) => {
            const invalid = x.key === "vat" && !isValidVatIT(form[x.key]);
            return (
              <div key={x.key} className={x.full ? "md:col-span-2" : ""}>
                <label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">{x.label}</label>
                {x.type === "textarea" ? (
                  <textarea data-testid={`az-${x.key}`} rows={3}
                    className={`${inp} mt-1.5`}
                    value={form[x.key]} onChange={(e) => set(x.key, e.target.value)} />
                ) : (
                  <input data-testid={`az-${x.key}`} type={x.type || "text"}
                    className={`${inp} mt-1.5 ${invalid ? "border-alert ring-1 ring-alert/40" : ""}`}
                    value={form[x.key]} onChange={(e) => set(x.key, e.target.value)} />
                )}
                {invalid && <p data-testid="az-vat-error" className="text-xs text-alert mt-1">P. IVA non valida (11 cifre, checksum)</p>}
              </div>
            );
          })}
        </div>
        <div className="mt-6 flex justify-end">
          <button data-testid="az-save-btn" onClick={save} disabled={saving} className="rounded-full bg-brand text-white px-6 py-2.5 text-sm font-semibold flex items-center gap-2 hover:bg-brand-hover disabled:opacity-60">
            <Save size={16} /> {saving ? "Salvataggio…" : "Salva"}
          </button>
        </div>
      </div>
    </div>
  );
}
